import { useQuery, useQueries } from '@tanstack/react-query'
import { moduleService, progressService, userService } from '../../services'
import { absenceService, AbsenceRequestStatus } from '../../services/absence.service'
import { useAuth } from '../../auth/auth.context'
import PageLayout from '../../components/layout/page-layout'
import { BulletinBoardDisplay } from '../../components/bulletin-board-display'
import { Link } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Book,
  CalendarDays,
  ChevronRight,
  CircleAlert,
  ClipboardList,
  Search,
  Sparkles,
  Users,
} from 'lucide-react'
import { Container } from '../../components/ui/container'
import Table from '../../components/ui/table'
import { useLocale } from '../../hooks/use-locale'
import { LinkStatCard } from '../../components/dashboard/link-stat-card'
import {
  DashboardSection,
  DashboardLinkRow,
  DashboardModuleCard,
} from '../../components/dashboard/dashboard-section'
import { PageHeader } from '../../components/layout/page-header'
import { PagePanel } from '../../components/layout/page-panel'
import { pageInput, pageLink } from '../../components/layout/page-styles'
import { cn } from '../../lib/utils'
import { FEATURES } from '../../config/features.config'

export default function AdminDashboard() {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const { user, company } = useAuth()
  const [showAllEmployees, setShowAllEmployees] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const getCompletionDotClasses = (percent: number) => {
    if (percent >= 80) return ['bg-emerald-500', 'bg-emerald-500', 'bg-emerald-500']
    if (percent >= 50) return ['bg-emerald-500', 'bg-emerald-500', 'bg-gray-300']
    if (percent > 0) return ['bg-rose-500', 'bg-gray-300', 'bg-gray-300']
    return ['bg-gray-300', 'bg-gray-300', 'bg-gray-300']
  }

  const getMissingInfoTone = (issue: string) => {
    const text = issue.toLowerCase()
    if (text.includes('start') || text.includes('profil') || text.includes('steuer')) {
      return {
        iconBg: 'bg-rose-100',
        iconText: 'text-rose-700',
      }
    }
    return {
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-700',
    }
  }

  const {
    data: modules,
    isLoading: modulesLoading,
    error: modulesError,
  } = useQuery({
    queryKey: ['company-modules'],
    queryFn: () => moduleService.getModules({ companyId: user?.companyId }),
  })

  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['company-employees', user?.companyId],
    queryFn: () =>
      userService.getUsers({
        companyId: user?.companyId,
        limit: 100,
      }),
    enabled: !!user?.companyId,
  })

  const { data: absenceRequests } = useQuery({
    queryKey: ['absence-requests'],
    queryFn: () => absenceService.getAllRequests(),
    enabled: FEATURES.absenceRequests,
  })

  const allEmployees = useMemo(() => {
    if (!employeesData?.users) return []
    return employeesData.users.filter(emp => emp.role === 'EMPLOYEE')
  }, [employeesData?.users])

  const allEmployeesProgressQueries = useQueries({
    queries:
      allEmployees.map(emp => ({
        queryKey: ['employee-progress', emp.id],
        queryFn: () => progressService.getUserProgress(emp.id!),
        enabled: !!emp.id,
        retry: 1,
        staleTime: 30000,
      })) || [],
  })

  const allModuleAssignmentsQueries = useQueries({
    queries:
      modules?.modules?.map(module => ({
        queryKey: ['module-assignments', module.id],
        queryFn: () => moduleService.getModuleAssignments(module.id),
        enabled: !!module.id,
      })) || [],
  })

  const employeesWithStats = useMemo(() => {
    if (!allEmployees.length || !modules?.modules) return []

    return allEmployees.map((employee, empIndex) => {
      const progressQuery = allEmployeesProgressQueries[empIndex]
      const progress = progressQuery?.data || []
      const hasProgressError = progressQuery?.isError
      const completedModuleIds = new Set(progress.filter(p => p.completed).map(p => p.moduleId))

      const assigned: string[] = []
      allModuleAssignmentsQueries.forEach((query, modIndex) => {
        if (query.data && !query.isLoading) {
          const assignments = query.data || []
          const module = modules.modules[modIndex]
          const employeeId = employee.id
          if (
            module &&
            employeeId &&
            assignments.some(a => (a.userId || a.user?.id) === employeeId)
          ) {
            assigned.push(module.id)
          }
        }
      })

      const completed = assigned.filter(moduleId => completedModuleIds.has(moduleId)).length
      const total = assigned.length
      const missing = total - completed

      let needsAttention = false
      if (employee.startDate) {
        const startDate = new Date(employee.startDate)
        const today = new Date()
        const daysUntilStart = Math.ceil(
          (startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        )
        needsAttention = daysUntilStart < 3 && daysUntilStart >= 0 && missing > 0
      }

      return {
        employee,
        progress,
        stats: { completed, missing, total },
        needsAttention,
        hasProgressError,
      }
    })
  }, [allEmployees, allEmployeesProgressQueries, allModuleAssignmentsQueries, modules?.modules])

  const filteredAndSortedEmployees = useMemo(() => {
    let filtered = employeesWithStats

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        ({ employee }) =>
          employee.firstName.toLowerCase().includes(query) ||
          employee.lastName.toLowerCase().includes(query) ||
          (employee.email?.toLowerCase().includes(query) ?? false)
      )
    }

    filtered = [...filtered].sort((a, b) => {
      const startA = a.employee.startDate ? new Date(a.employee.startDate).getTime() : Infinity
      const startB = b.employee.startDate ? new Date(b.employee.startDate).getTime() : Infinity
      if (startA !== startB) return startA - startB
      const nameA = `${a.employee.firstName} ${a.employee.lastName}`.toLowerCase()
      const nameB = `${b.employee.firstName} ${b.employee.lastName}`.toLowerCase()
      return nameA.localeCompare(nameB)
    })

    return filtered
  }, [employeesWithStats, searchQuery])

  const displayedEmployees = useMemo(() => {
    if (showAllEmployees) return filteredAndSortedEmployees
    return filteredAndSortedEmployees.slice(0, 8)
  }, [filteredAndSortedEmployees, showAllEmployees])

  const companyLimits = (company as any)?.limits as { maxLicenses: number } | undefined

  const overallStats = useMemo(() => {
    const totalEmployees = allEmployees.length
    const employeesNeedingAttention = employeesWithStats.filter(e => e.needsAttention).length
    const totalModules = modules?.modules?.length || 0
    const incompleteEmployees = employeesWithStats.filter(
      e => e.stats.total > 0 && e.stats.missing > 0
    ).length
    const completedEmployees = employeesWithStats.filter(
      e => e.stats.total > 0 && e.stats.missing === 0
    ).length
    const completionRate =
      totalEmployees > 0 ? Math.round((completedEmployees / totalEmployees) * 100) : 0

    const pendingAbsence = (absenceRequests || []).filter(
      r => r.status === AbsenceRequestStatus.PENDING
    ).length

    return {
      totalEmployees,
      employeesNeedingAttention,
      totalModules,
      incompleteEmployees,
      completedEmployees,
      completionRate,
      pendingAbsence,
    }
  }, [allEmployees.length, employeesWithStats, modules?.modules, absenceRequests])

  const moduleCards = useMemo(() => {
    if (!modules?.modules?.length) return []

    const completedByUserId = new Map<string, Set<string>>()
    for (const entry of employeesWithStats) {
      const userId = entry.employee.id
      if (!userId) continue
      completedByUserId.set(
        userId,
        new Set(entry.progress.filter(p => p.completed).map(p => p.moduleId))
      )
    }

    return modules.modules.slice(0, 4).map((m, index) => {
      const assignments = allModuleAssignmentsQueries[index]?.data || []
      const assignedUserIds = assignments
        .map(a => (a.userId || a.user?.id) as string | undefined)
        .filter(Boolean) as string[]
      const assignedUnique = Array.from(new Set(assignedUserIds))

      const employeeIds = new Set(allEmployees.map(e => e.id))
      const assignedEmployees = assignedUnique.filter(uid => employeeIds.has(uid))

      let completed = 0
      for (const uid of assignedEmployees) {
        const completedSet = completedByUserId.get(uid)
        if (completedSet?.has(m.id)) completed += 1
      }

      const total = assignedEmployees.length
      const percent = total > 0 ? Math.round((completed / total) * 100) : 0

      return {
        id: m.id,
        title: m.title || t('common.moduleDefault'),
        assigned: total,
        completed,
        percent,
      }
    })
  }, [modules?.modules, allModuleAssignmentsQueries, employeesWithStats, t])

  const missingInfoItems = useMemo(() => {
    const candidates = allEmployees
      .filter(e => {
        return !e.phoneNumber || !e.function || !e.startDate
      })
      .slice(0, 4)

    return candidates.map(e => {
      const issues: string[] = []
      if (!e.phoneNumber) issues.push(t('common.phoneMissing'))
      if (!e.function) issues.push(t('common.functionMissing'))
      if (!e.startDate) issues.push(t('common.startDateMissing'))
      return {
        id: e.id || '',
        name: `${e.firstName} ${e.lastName}`,
        issue: issues[0] || t('common.incompleteProfile'),
      }
    })
  }, [allEmployees, t])

  const hasActionItems =
    missingInfoItems.length > 0 ||
    (FEATURES.absenceRequests && overallStats.pendingAbsence > 0)

  return (
    <PageLayout
      headerProps={{
        title: t('admin.dashboardTitle'),
        subtitle: company ? company.name : undefined,
        variant: 'dashboard',
      }}
      isLoading={modulesLoading || employeesLoading}
      error={modulesError}
      loadingMessage={t('admin.loadingDashboard')}
      errorMessage={t('errors.loadDashboard')}
    >
      <Container>
        <PageHeader
          title={`${t('auth.welcomeBack')}${user?.firstName ? `, ${user.firstName}` : ''}`}
          subtitle={company?.name}
        />

        {user?.companyId ? <BulletinBoardDisplay companyId={user.companyId} /> : null}

        {companyLimits &&
        (overallStats.totalEmployees >= companyLimits.maxLicenses ||
          overallStats.totalModules >= companyLimits.maxLicenses) ? (
          <div className='rounded-xl border border-error-200 bg-error-50/80 px-5 py-4 text-sm text-error-800 flex items-center justify-between gap-3'>
            <span>{t('common.limitReached')}</span>
            <Link
              to='/subscription'
              className='inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-white text-error-700 border border-error-200 hover:bg-error-100 transition-colors'
            >
              {t('common.increaseLimit')}
            </Link>
          </div>
        ) : null}

        {/* KPI overview */}
        <div
          className={cn(
            'grid grid-cols-1 sm:grid-cols-2 gap-4',
            FEATURES.absenceRequests ? 'xl:grid-cols-4' : 'xl:grid-cols-3'
          )}
        >
          <LinkStatCard
            to='/users'
            icon={Users}
            label={t('common.employees')}
            value={
              <>
                {overallStats.totalEmployees}
                {companyLimits ? (
                  <span className='text-lg font-semibold text-neutral-400'>
                    {' / '}
                    {companyLimits.maxLicenses}
                  </span>
                ) : null}
              </>
            }
            iconBgClassName='bg-primary-50 ring-1 ring-primary-100'
            iconClassName='text-primary-600'
            hint={
              companyLimits && overallStats.totalEmployees >= companyLimits.maxLicenses
                ? t('common.limitReached')
                : overallStats.employeesNeedingAttention > 0
                  ? t('common.needsAttentionCount', {
                      count: overallStats.employeesNeedingAttention,
                    })
                  : companyLimits
                    ? t('common.availableCount', {
                        count: companyLimits.maxLicenses - overallStats.totalEmployees,
                      })
                    : t('common.allGood')
            }
          />

          <LinkStatCard
            to='/modules'
            icon={Book}
            label={t('modules.moduleLicenses')}
            value={
              <>
                {overallStats.totalModules}
                {companyLimits ? (
                  <span className='text-lg font-semibold text-neutral-400'>
                    {' / '}
                    {companyLimits.maxLicenses}
                  </span>
                ) : null}
              </>
            }
            iconBgClassName='bg-emerald-50 ring-1 ring-emerald-100'
            iconClassName='text-emerald-600'
            hint={
              companyLimits
                ? overallStats.totalModules >= companyLimits.maxLicenses
                  ? t('common.limitReached')
                  : t('common.availableCount', {
                      count: companyLimits.maxLicenses - overallStats.totalModules,
                    })
                : t('common.allGood')
            }
          />

          <LinkStatCard
            to='/users'
            icon={CircleAlert}
            label={t('modules.incompleteEmployees')}
            value={overallStats.incompleteEmployees}
            iconClassName='text-rose-600'
            iconBgClassName='bg-rose-50 ring-1 ring-rose-100'
            hint={t('common.completionRate', { rate: overallStats.completionRate })}
          />

          {FEATURES.absenceRequests ? (
            <LinkStatCard
              to='/absence-requests'
              icon={ClipboardList}
              label={t('modules.openHrTasks')}
              value={overallStats.pendingAbsence}
              iconClassName='text-amber-600'
              iconBgClassName='bg-amber-50 ring-1 ring-amber-100'
              hint={t('common.reviewRequests')}
            />
          ) : null}
        </div>

        {/* Content sections: Modules + Action items side by side */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 items-start'>
          <DashboardSection
            title={t('modules.modulesAndProcesses')}
            subtitle={moduleCards.length < (modules?.modules?.length || 0) ? t('common.showingFirst', { count: moduleCards.length }) : undefined}
            icon={
              <span className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-primary-100'>
                <Sparkles className='h-5 w-5' />
              </span>
            }
            viewAllTo='/modules'
            viewAllLabel={t('common.viewAll')}
          >
            {moduleCards.length > 0 ? (
              <div className='grid grid-cols-1 gap-3'>
                {moduleCards.map(card => (
                  <DashboardModuleCard
                    key={card.id}
                    to={`/modules/${card.id}`}
                    title={card.title}
                    assigned={card.assigned}
                    completed={card.completed}
                    percent={card.percent}
                    labels={{
                      active: t('common.activeCount'),
                      completed: t('common.completed'),
                      open: t('common.openCount'),
                      viewModule: t('common.viewModule', { title: card.title }),
                    }}
                  />
                ))}
              </div>
            ) : (
              <p className='text-sm text-neutral-500'>{t('modules.noModulesAvailable')}</p>
            )}
          </DashboardSection>

          <DashboardSection
            title={t('modules.personnelDataAndHr')}
            icon={
              <span className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-primary-100'>
                <ClipboardList className='h-5 w-5' />
              </span>
            }
            viewAllTo='/users'
            viewAllLabel={t('common.viewAll')}
          >
            <div className='space-y-3'>
              {hasActionItems ? (
                <>
                  {missingInfoItems.length > 0 ? (
                    <div className='space-y-2'>
                      <h3 className='text-xs font-semibold text-neutral-500 uppercase tracking-wide'>
                        {t('modules.missingInformation')}
                      </h3>
                      {missingInfoItems.map(item => {
                        if (!item.id) return null
                        const tone = getMissingInfoTone(item.issue)
                        return (
                          <DashboardLinkRow
                            key={item.id}
                            to={`/users/${item.id}`}
                            title={item.name}
                            subtitle={item.issue}
                            actionLabel={t('common.openPersonnelData')}
                            icon={
                              <div
                                className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tone.iconBg} ${tone.iconText}`}
                              >
                                <CircleAlert className='h-5 w-5' />
                              </div>
                            }
                          />
                        )
                      })}
                    </div>
                  ) : null}

                  {FEATURES.absenceRequests ? (
                    <DashboardLinkRow
                      to='/absence-requests'
                      title={t('modules.absenceRequests')}
                      subtitle={t('common.pendingCount', { count: overallStats.pendingAbsence })}
                      actionLabel={t('common.openAction')}
                      icon={
                        <div className='h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-50 text-primary-600 ring-1 ring-primary-100'>
                          <CalendarDays className='h-5 w-5' />
                        </div>
                      }
                    />
                  ) : null}
                </>
              ) : (
                <p className='text-sm text-neutral-500'>{t('modules.noOpenProfileEntries')}</p>
              )}
            </div>
          </DashboardSection>
        </div>

        {/* Employees table — full width */}
        <PagePanel padding={false}>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-4 border-b border-primary-100 bg-surface-secondary'>
            <h2 className='text-base font-semibold text-neutral-900'>{t('common.employees')}</h2>
            <div className='flex items-center gap-3'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400' />
                <input
                  type='text'
                  placeholder={t('common.search')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className={cn(pageInput, 'w-72 max-w-full')}
                />
              </div>
              <Link to='/users' className={cn(pageLink, 'inline-flex items-center gap-0.5')}>
                {t('common.viewAll')}
                <ChevronRight className='w-4 h-4' />
              </Link>
            </div>
          </div>

          <Table
            embedded
            data={displayedEmployees}
            rowKey={(row, i) => row.employee.id || String(i)}
            getRowHref={row => (row.employee.id ? `/users/${row.employee.id}` : undefined)}
            columns={[
              {
                header: t('common.employees'),
                cell: ({ employee, needsAttention }) => (
                  <div className='min-w-0'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <span className='font-medium truncate'>
                        {employee.firstName} {employee.lastName}
                      </span>
                      {needsAttention && (
                        <span className='text-yellow-600' title={t('common.attentionRequired')}>
                          ⚠️
                        </span>
                      )}
                    </div>
                    <div className='text-xs text-neutral-500 truncate'>{employee.email}</div>
                  </div>
                ),
              },
              {
                header: t('common.start'),
                className: 'whitespace-nowrap',
                cell: ({ employee }) =>
                  employee.startDate
                    ? formatDate(employee.startDate, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })
                    : t('common.dash'),
              },
              {
                header: t('modules.onboarding'),
                cell: ({ stats, hasProgressError }) => {
                  if (hasProgressError) {
                    return (
                      <div className='min-w-[140px]'>
                        <span className='text-xs text-error-600'>
                          {t('errors.loadProgressFailed')}
                        </span>
                      </div>
                    )
                  }
                  const percent =
                    stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0
                  const dots = getCompletionDotClasses(percent)
                  return (
                    <div className='min-w-[140px]'>
                      <div className='flex items-center justify-between text-xs text-neutral-600'>
                        <span>
                          {stats.total > 0
                            ? `${stats.completed}/${stats.total}`
                            : t('common.dash')}
                        </span>
                        <span className='font-medium text-neutral-900'>{percent}%</span>
                      </div>
                      <div className='mt-1 flex items-center gap-1'>
                        {dots.map((c, idx) => (
                          <span key={idx} className={`h-1.5 w-1.5 rounded-full ${c}`} />
                        ))}
                      </div>
                      <div className='mt-2 h-2 w-full rounded-full bg-primary-50 overflow-hidden'>
                        <div
                          className='h-full rounded-full bg-gradient-to-r from-primary-300 to-primary-500'
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  )
                },
              },
              {
                header: t('common.daysHeader'),
                className: 'whitespace-nowrap',
                cell: ({ employee }) => {
                  if (!employee.startDate) return t('common.dash')
                  const start = new Date(employee.startDate)
                  const today = new Date()
                  const days = Math.ceil(
                    (start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
                  )
                  return t('common.days', { count: Math.max(days, 0) })
                },
              },
              {
                header: '',
                className: 'text-right whitespace-nowrap w-10',
                cell: () => (
                  <ChevronRight className='inline-block w-4 h-4 text-neutral-300' aria-hidden />
                ),
              },
            ]}
            emptyMessage={searchQuery ? t('users.empty') : t('modules.noEmployeesAvailable')}
          />

          {filteredAndSortedEmployees.length > 8 ? (
            <button
              onClick={() => setShowAllEmployees(!showAllEmployees)}
              className='mx-6 mb-6 w-[calc(100%-3rem)] px-4 py-2.5 bg-surface-secondary text-neutral-700 hover:bg-surface-card-hover border border-primary-100 rounded-xl font-medium transition-colors text-sm'
            >
              {showAllEmployees
                ? t('common.showLess')
                : t('common.showAllCount', { count: filteredAndSortedEmployees.length })}
            </button>
          ) : null}
        </PagePanel>
      </Container>
    </PageLayout>
  )
}
